import { MessageComposerDialog } from "@/components/ui/message-composer-dialog";
import { useToast } from "@/components/ui/use-toast.ts";
import {
  ReviewMentor,
  useSendMentorMessage,
} from "@/lib/api/training-simulator/training-simulator-review";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { Button } from "@somethings-ui/web";
import { useState } from "react";

interface MessageMentorProps {
  mentor: ReviewMentor;
  className?: string;
}

export function MessageMentor({ mentor, className }: MessageMentorProps) {
  const [open, setOpen] = useState(false);
  const { track } = useTrackEvent();
  return (
    <>
      <Button
        onClick={() => {
          track(PostHogEvents.SIMULATOR_REVIEW_MESSAGE_MENTOR_MODAL_OPEN, {
            trainee_mentor_id: mentor.id,
          });
          setOpen(true);
        }}
        variant="secondary"
        fullWidth={false}
        className={className}
        size="sm"
      >
        Send message
      </Button>
      <MessageMentorModal
        open={open}
        onOpenChange={setOpen}
        mentor={mentor}
      />
    </>
  );
}

interface MessageMentorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mentor: ReviewMentor;
}

function MessageMentorModal({
  open,
  onOpenChange,
  mentor,
}: MessageMentorModalProps) {
  const { toast } = useToast();
  const { track } = useTrackEvent();
  const { mutateAsync: sendMessage, isPending } = useSendMentorMessage(
    mentor.id,
  );

  const handleSend = async (message: string) => {
    try {
      await sendMessage({ message });
      track(PostHogEvents.SIMULATOR_LM_SEND_MESSAGE_CLICK, {
        trainee_mentor_id: mentor.id,
      });
      toast({
        title: "Your message has been sent to the mentor",
        description: "Check out the support chat to view it.",
      });
    } catch (error) {
      toast({
        title: "Couldn't send message",
        description: "Please try again.",
        variant: "destructive",
      });
      throw error;
    }
  };

  return (
    <MessageComposerDialog
      open={open}
      title={`Message ${mentor.name || "Mentor"}`}
      description="Leave feedback for the entire performance or ask for help with this level."
      placeholder="Type your message..."
      isPending={isPending}
      onOpenChange={onOpenChange}
      onSubmit={handleSend}
    />
  );
}
